import { playersAbove } from "../document/band.ts";
import type { MapDocument, PropInstance, TileRef } from "../document/types.ts";
import { artHeight, artLeft, artTop, artWidth } from "./art-box.ts";
import { isFlatFrame, propBaseLine, propFlatLine, type FrameSize } from "./prop-hit.ts";
import type { PropRef } from "./templates.ts";

/**
 * "Sort just in front of what is behind" (ADR-040's amendment of 2026-10-06, decision 12): the sort offset that
 * puts a placed prop's line one pixel south of the southernmost line among the props behind it, so the prop
 * still draws over them and a character standing in front of them draws over the prop. It is a number worked
 * out once from where the props stand: it is written as an ordinary sort offset and does not follow a prop
 * that is moved later.
 *
 * A prop is behind another when their art boxes share pixels (the opaque bounds above the base, mirrored with
 * the flips, as `art-box.ts` places them) and its line, its base line plus its sort offset, is north of the
 * other's base line taken without its offset. Lines are measured as `propSortRow` measures them, so the editor
 * and the game rank the result alike (ADR-071). A prop is ranked by the frame it is placed with.
 */

/**
 * A frame as the sort needs it: its size, base and art bounds, and whether it draws under every prop (the
 * asset manifest's `ground` without `amongProps`), in which case it is under whatever it overlaps already. A frame
 * with both is flat (`isFlatFrame`): a prop with a line of its own, the top of its art.
 */
export type SortFrame = FrameSize & { readonly ground?: true };

/**
 * Looks up a frame, undefined for one the lookup does not hold.
 */
export type SortFrameOf = (frame: TileRef) => SortFrame | undefined;

/**
 * Why a prop gets no offset: nothing its art overlaps is behind it; it already carries the offset; its frame
 * draws under every prop; or its layer is under the Players band, where nothing sorts against characters.
 */
export type SortInFrontReason = "nothing-behind" | "already-there" | "under-every-prop" | "under-the-band";

/**
 * The offset to write and the line it gives in world pixels, or why there is none.
 */
export type SortInFrontResult = { readonly sortPixels: number; readonly line: number } | { readonly reason: SortInFrontReason };

/**
 * One selected prop with its layer and its result.
 */
export interface SortInFrontEntry {
  readonly layerId: string;
  readonly prop: PropInstance;
  readonly result: SortInFrontResult;
}

/**
 * How many props of a plan get an offset and how many are left for each reason.
 */
export interface SortInFrontCounts {
  readonly sorted: number;
  readonly nothingBehind: number;
  readonly alreadyThere: number;
  readonly underEveryProp: number;
  readonly underTheBand: number;
}

/**
 * A prop as it stands: its art box in world pixels, right and bottom exclusive, and the line it sorts by.
 */
interface Standing {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly line: number;
}

/**
 * The line a prop is ranked by before its sort offset: its base line, or for a flat frame the top of its art.
 *
 * @param prop The prop.
 * @param frame Its frame.
 * @param tileSize Tile size in pixels.
 * @returns The line in world pixels.
 */
function lineOf(prop: PropInstance, frame: SortFrame, tileSize: number): number {
  return isFlatFrame(frame) ? propFlatLine(prop, frame, tileSize) : propBaseLine(prop, frame, tileSize);
}

/**
 * Whether a frame draws under every prop: flagged `ground` and not flat.
 *
 * @param frame The frame.
 * @returns True for a frame at both boxes.
 */
function underEveryProp(frame: SortFrame): boolean {
  return frame.ground === true && !isFlatFrame(frame);
}

/**
 * Where a prop stands.
 *
 * @param prop The prop.
 * @param frame Its frame.
 * @param tileSize Tile size in pixels.
 * @returns Its art box and its line, the sort offset included.
 */
function standingOf(prop: PropInstance, frame: SortFrame, tileSize: number): Standing {
  const left = artLeft(prop, frame, tileSize);
  const top = artTop(prop, frame, tileSize);
  return { left, top, right: left + artWidth(frame), bottom: top + artHeight(frame), line: lineOf(prop, frame, tileSize) + prop.sortPixels };
}

/**
 * The props a prop could stand against, as they stand: those the lookup sizes whose frame does not draw under
 * every prop.
 *
 * @param props The props.
 * @param frameOf Frame lookup.
 * @param tileSize Tile size in pixels.
 * @returns Where each stands.
 */
function standings(props: readonly PropInstance[], frameOf: SortFrameOf, tileSize: number): Standing[] {
  return props.flatMap((prop) => {
    const frame = frameOf(prop.frame);
    return frame === undefined || underEveryProp(frame) ? [] : [standingOf(prop, frame, tileSize)];
  });
}

/**
 * A prop's result against props as they stand.
 *
 * @param prop The prop.
 * @param others Where the props it could stand against stand.
 * @param frameOf Frame lookup.
 * @param tileSize Tile size in pixels.
 * @returns The offset, or why there is none.
 */
function resolve(prop: PropInstance, others: readonly Standing[], frameOf: SortFrameOf, tileSize: number): SortInFrontResult {
  const frame = frameOf(prop.frame);
  if (frame === undefined) {
    return { reason: "nothing-behind" };
  }
  if (underEveryProp(frame)) {
    return { reason: "under-every-prop" };
  }
  const own = standingOf(prop, frame, tileSize);
  const base = lineOf(prop, frame, tileSize);
  let southernmost = Number.NEGATIVE_INFINITY;
  for (const other of others) {
    if (other.line < base && other.line > southernmost && other.left < own.right && own.left < other.right && other.top < own.bottom && own.top < other.bottom) {
      southernmost = other.line;
    }
  }
  if (southernmost === Number.NEGATIVE_INFINITY) {
    return { reason: "nothing-behind" };
  }
  const sortPixels = Math.floor(southernmost - base) + 1;
  return sortPixels === prop.sortPixels ? { reason: "already-there" } : { sortPixels, line: base + sortPixels };
}

/**
 * The sort offset that puts a prop's line one pixel south of the southernmost line among the props behind it:
 * the least whole number of pixels that leaves its line south of that line, never positive, measured from its
 * base line whatever offset it carries now.
 *
 * @param prop The prop.
 * @param others The props it could stand against, the prop itself left out; a frame the lookup does not hold
 *   and a frame that draws under every prop are passed over.
 * @param frameOf Frame lookup.
 * @param tileSize Tile size in pixels.
 * @returns The offset and the line it gives, or the reason there is none: `under-every-prop` for a prop whose
 *   own frame draws under every prop, `nothing-behind` when no prop behind it overlaps its art (and for a prop
 *   whose own frame the lookup does not hold, which a plan leaves out before asking), `already-there` when it
 *   carries the offset.
 */
export function sortInFront(prop: PropInstance, others: readonly PropInstance[], frameOf: SortFrameOf, tileSize: number): SortInFrontResult {
  return resolve(prop, standings(others, frameOf, tileSize), frameOf, tileSize);
}

/**
 * The plan for a selection: each selected prop's result against the props of the Players band's prop layers
 * that are not selected, as they stand now, so neither the selection's order nor a layer's prop order changes
 * what a prop gets. Hidden and locked layers count, since the game draws them. A selected prop on a layer
 * under the band gets `under-the-band`; a ref the map does not hold and a prop whose own frame the lookup does
 * not hold are left out, since nothing can be said of them.
 *
 * @param map The map.
 * @param refs The selected props.
 * @param frameOf Frame lookup.
 * @param tileSize Tile size in pixels.
 * @returns One entry per selected prop found and sized, in the selection's order.
 */
export function planSortInFront(map: MapDocument, refs: readonly PropRef[], frameOf: SortFrameOf, tileSize: number): SortInFrontEntry[] {
  const band = playersAbove(map);
  const selected = new Set(refs.map((ref) => `${ref.layerId}\u0000${ref.propId}`));
  const others = map.layers.flatMap((layer, index) =>
    layer.kind === "prop" && index >= band
      ? standings(
          layer.props.filter((prop) => !selected.has(`${layer.id}\u0000${prop.id}`)),
          frameOf,
          tileSize,
        )
      : [],
  );
  return refs.flatMap((ref) => {
    const index = map.layers.findIndex((layer) => layer.id === ref.layerId);
    const layer = map.layers[index];
    const prop = layer?.kind === "prop" ? layer.props.find((p) => p.id === ref.propId) : undefined;
    if (prop === undefined || frameOf(prop.frame) === undefined) {
      return [];
    }
    return [{ layerId: ref.layerId, prop, result: index < band ? { reason: "under-the-band" as const } : resolve(prop, others, frameOf, tileSize) }];
  });
}

/**
 * Counts a plan's entries by what each prop gets.
 *
 * @param entries The plan.
 * @returns The counts.
 */
export function sortInFrontCounts(entries: readonly SortInFrontEntry[]): SortInFrontCounts {
  /**
   * How many entries carry a reason.
   *
   * @param reason The reason.
   * @returns The count.
   */
  const left = (reason: SortInFrontReason): number => entries.filter((entry) => "reason" in entry.result && entry.result.reason === reason).length;
  return {
    sorted: entries.filter((entry) => "sortPixels" in entry.result).length,
    nothingBehind: left("nothing-behind"),
    alreadyThere: left("already-there"),
    underEveryProp: left("under-every-prop"),
    underTheBand: left("under-the-band"),
  };
}
